import type { Ref } from 'react';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

type Size = 'sm';

const sizeClassNames: Record<Size, string> = {
  sm: 'form-input-sm',
};

interface NumericFieldProps {
  name: string;
  value: number | null | undefined;
  size?: Size;
  className?: string;
  placeholder?: string;
  onChange: (value: number | null) => void;
  onBlur?: () => void;
  disabled?: boolean;
  ref?: Ref<HTMLInputElement>;
}

export const NumericField = ({
  name,
  value,
  size,
  className,
  placeholder,
  onChange,
  onBlur,
  disabled,
  ref,
}: NumericFieldProps) => {
  const intl = useIntl();
  const formatParts = intl.formatNumberToParts(1234.56);
  const thosandSeparator = formatParts.find((part) => part.type === 'group')?.value ?? '.';
  const decimalSeparator = formatParts.find((part) => part.type === 'decimal')?.value ?? ',';

  const [internalValue, setInternalValue] = useState<string>(
    typeof value === 'number'
      ? value.toLocaleString(intl.locale).replaceAll(thosandSeparator, '')
      : '',
  );

  useEffect(() => {
    setInternalValue(
      typeof value === 'number'
        ? value.toLocaleString(intl.locale).replaceAll(thosandSeparator, '')
        : '',
    );
  }, [value, intl.locale, thosandSeparator]);

  const internalOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const stringValue = e.target.value.replace(thosandSeparator, '');

    const regex = new RegExp(`^\\d*(?:\\${decimalSeparator}\\d{0,2})?$`);
    const regexTest = regex.test(stringValue);

    if (regexTest) {
      setInternalValue(stringValue);
      const parsedValue = parseFloat(stringValue.replace(decimalSeparator, '.'));
      const finalValue = stringValue === '' ? null : parsedValue;
      onChange(finalValue);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  return (
    <input
      autoComplete='off'
      type='text'
      ref={ref}
      name={name}
      data-testid={`numeric-field-${name}`}
      onFocus={handleFocus}
      onBlur={onBlur}
      onChange={internalOnChange}
      value={internalValue}
      className={`form-input max-w-32 min-w-24 text-right ${size ? sizeClassNames[size] : ''} ${className ?? ''} ${
        disabled
          ? 'dark:!bg-c4 cursor-not-allowed !bg-gray-100 !text-gray-500 opacity-80 dark:!text-gray-500'
          : ''
      }`}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
};
